import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import Logo from "@/components/Logo";
import { Ship, Sun, Moon, Loader2, ShieldCheck, Waves, KeyRound } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1561728130-afd430af0493?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { setUser } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("creds"); // creds | twofa
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [phrase, setPhrase] = useState("");
  const [remember, setRemember] = useState(true);
  const invite = params.get("invite");

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setChallenge(data.challenge);
        setStage("twofa");
      } else if (data.requires_phrase) {
        setChallenge(data.challenge);
        setStage("phrase");
      } else {
        setUser(data.user);
        toast.success(`Welcome aboard, ${data.user.name.split(" ")[0]}`);
        navigate("/dashboard");
      }
    } catch (e2) { setError(errMsg(e2)); }
    finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login/verify-2fa", { challenge, code });
      if (data.requires_phrase) { setChallenge(data.challenge); setStage("phrase"); return; }
      setUser(data.user);
      toast.success("Identity verified");
      navigate("/dashboard");
    } catch (e2) { setError(errMsg(e2)); }
    finally { setLoading(false); }
  };

  const submitPhrase = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login/verify-phrase", { challenge, phrase, remember_device: remember });
      setUser(data.user);
      toast.success("Identity verified — welcome back");
      navigate("/dashboard");
    } catch (e2) { setError(errMsg(e2)); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Hero */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={HERO} alt="Galleon at sea" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030A16] via-[#030A16]/70 to-[#030A16]/30" />
        <div className="relative z-10 flex h-full flex-col justify-end p-12">
          <div>
            <img src="/ban-logo.jpg" alt="Brothers Across Nigeria Int'l — Brother Deliver Brother" className="mb-6 h-44 w-44 rounded-full object-cover shadow-2xl ring-4 ring-[hsl(var(--gold))]/50 animate-float" data-testid="login-hero-logo" />
            <p className="label-eyebrow">Private Membership Portal</p>
            <h1 className="mt-3 font-display text-5xl font-800 leading-tight text-white">
              Steer the <span className="gold-text">Vessel</span><br />Together
            </h1>
            <p className="mt-4 max-w-md text-sm text-slate-300">
              A calm, secure harbour for Brothers Across Nigeria Int'l under the Iota Galleon —
              three frigates, one brotherhood. Directory, feed, governance and finances in one place.
            </p>
            <div className="mt-6 flex gap-6 text-xs text-slate-400">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--gold))]" /> 2FA Secured</span>
              <span className="flex items-center gap-2"><Waves className="h-4 w-4 text-[hsl(var(--water))]" /> Members Only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="sea-bg flex w-full flex-col items-center justify-center p-6 lg:w-1/2">
        <div className="absolute right-4 top-4">
          <Button variant="ghost" size="icon" onClick={toggle} className="text-[hsl(var(--gold))]" data-testid="theme-toggle-button">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Logo /></div>

          {stage === "creds" ? (
            <form onSubmit={submitCreds} className="glass rounded-2xl p-8 shadow-2xl" data-testid="login-form">
              <div className="mb-6">
                <p className="label-eyebrow">Secure Access</p>
                <h2 className="mt-1 font-display text-3xl font-700">Sign In</h2>
                <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to board the vessel.</p>
              </div>
              {invite && (
                <div className="mb-4 rounded-lg border border-[hsl(var(--water))]/40 bg-[hsl(var(--water))]/10 p-3 text-xs text-[hsl(var(--water))]">
                  Invitation detected. Please sign in with the credentials issued by your officer.
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="" className="mt-1.5" data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
                </div>
              </div>
              {error && <p className="mt-4 text-sm text-destructive" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} className="mt-6 w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F] hover:bg-[hsl(var(--gold))]/90" data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Board the Vessel"}
              </Button>
              <div className="mt-4 text-center">
                <Link to="/forgot-password" className="text-sm text-[hsl(var(--water))] hover:underline" data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
                <span className="mx-2 text-muted-foreground">·</span>
                <Link to="/signup" className="text-sm font-700 text-[hsl(var(--gold))] hover:underline" data-testid="signup-link">
                  New brother? Sign up
                </Link>
              </div>
            </form>
          ) : stage === "phrase" ? (
            <form onSubmit={submitPhrase} className="glass rounded-2xl p-8 shadow-2xl" data-testid="phrase-form">
              <div className="mb-6 text-center">
                <KeyRound className="mx-auto h-10 w-10 text-[hsl(var(--gold))]" />
                <h2 className="mt-2 font-display text-2xl font-700">Security Phrase</h2>
                <p className="mt-1 text-sm text-muted-foreground">New device detected. Enter the security word or phrase you set when your Member ID was issued.</p>
              </div>
              <Input type="password" autoFocus value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="Your security phrase" autoComplete="off" data-testid="login-phrase-input" />
              <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-device-checkbox" />Trust this device for 90 days</label>
              {error && <p className="mt-4 text-center text-sm text-destructive" data-testid="phrase-error">{error}</p>}
              <Button type="submit" disabled={loading || phrase.length < 4} className="mt-6 w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="verify-phrase-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Board"}
              </Button>
              <button type="button" onClick={() => { setStage("creds"); setPhrase(""); setError(""); }} className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground">Back to sign in</button>
              <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="phrase-help">Lost your phrase? Ask an officer — Command Deck → Members → Reset phrase. You will then set a new one.</p>
            </form>
          ) : (
            <form onSubmit={submit2fa} className="glass rounded-2xl p-8 shadow-2xl" data-testid="twofa-form">
              <div className="mb-6 text-center">
                <ShieldCheck className="mx-auto h-10 w-10 text-[hsl(var(--gold))]" />
                <h2 className="mt-2 font-display text-2xl font-700">Two-Factor Verification</h2>
                <p className="mt-1 text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-2fa-code-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {error && <p className="mt-4 text-center text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={loading || code.length < 6} className="mt-6 w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="verify-2fa-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
              </Button>
              <button type="button" onClick={() => { setStage("creds"); setCode(""); setError(""); }} className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground">
                Back to sign in
              </button>
            </form>
          )}
          <p className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
            <Ship className="h-3.5 w-3.5" /> Confidential system. Authorized brothers only. · <Link to="/privacy" className="hover:underline" data-testid="login-privacy-link">Privacy</Link> · <Link to="/support" className="hover:underline" data-testid="login-support-link">Help &amp; Support</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
